import { useState } from "react";
import { Brain, Sparkles, Loader2, RefreshCw, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../../lib/api";
import { MUSCLE_BY_ID, canonicalMuscleId } from "../../data/muscleAtlas";

/**
 * ClinicalInsightsInline — Iter AB
 *
 * Lightweight insights panel surfaced inside the New Session diagram step.
 * Pre-seeds the muscle set from worked_areas + treatmentMap.primary so the
 * therapist gets one-tap AI reasoning during a live session instead of having
 * to navigate to the Body Chart History page.
 *
 * Reuses POST /api/clinical-insights (server-side cached per session) so
 * tapping the button twice on the same selection costs zero workflow.
 */
export default function ClinicalInsightsInline({
  clientId,
  sessionId,
  workedAreas = [],
  treatmentMap = null,
}) {
  const [insights, setInsights] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(true);

  // Seed pool — primary treatment-map muscles take precedence, then worked_areas,
  // capped at 8 so the AI stays focused.
  const seed = (() => {
    const pool = new Set();
    (treatmentMap?.primary || []).forEach((m) => pool.add(m));
    (workedAreas || []).forEach((m) => pool.add(m));
    return Array.from(pool).slice(0, 8);
  })();

  const run = async (force = false) => {
    if (!clientId) { setError("Open this from a client's session."); return; }
    if (seed.length === 0) { setError("Pick at least one muscle on the diagram first."); return; }
    setBusy(true); setError("");
    try {
      const r = await api.post("/clinical-insights", {
        client_id: clientId,
        session_id: sessionId || null,
        muscle_ids: seed,
        force_regenerate: force,
      });
      setInsights(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate insights.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-3xl border border-purple-200/60 bg-gradient-to-br from-purple-50/60 to-white p-5 sm:p-6 mb-6"
      data-testid="inline-insights-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
            <Brain className="w-4 h-4 text-purple-700" strokeWidth={1.6} />
          </div>
          <div>
            <div className="font-heading text-lg text-ink-900 flex items-center gap-2">
              Clinical insights
              <span className="text-[9px] uppercase tracking-wider bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded-full font-bold">AI</span>
            </div>
            <p className="text-xs text-ink-700 mt-0.5">
              {seed.length > 0
                ? `AI reasoning for ${seed.length} muscle${seed.length === 1 ? "" : "s"} you have staged for today: ${seed.slice(0, 3).map(labelOf).join(", ")}${seed.length > 3 ? " +more" : ""}.`
                : "Pick muscles on the diagram (or generate a treatment map) and the AI will reason about compensations, related symptoms, and what to evaluate next."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {insights && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="inline-insights-toggle"
              className="min-h-[36px] px-3 rounded-full border border-paper-200 text-xs text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1"
            >
              {expanded ? <><ChevronUp className="w-3 h-3" /> Collapse</> : <><ChevronDown className="w-3 h-3" /> Expand</>}
            </button>
          )}
          {!insights && (
            <button
              type="button"
              onClick={() => run(false)}
              disabled={busy || seed.length === 0}
              data-testid="inline-insights-generate-btn"
              className="min-h-[40px] px-4 rounded-full bg-purple-700 hover:bg-purple-800 text-white font-medium inline-flex items-center gap-1.5 transition-all disabled:opacity-50 text-sm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Reasoning…" : "Ask the AI"}
            </button>
          )}
          {insights && (
            <button
              type="button"
              onClick={() => run(true)}
              disabled={busy}
              data-testid="inline-insights-regen-btn"
              className="min-h-[36px] px-3 rounded-full border border-paper-200 text-xs text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-900 flex gap-2" data-testid="inline-insights-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {insights && expanded && (
        <div className="space-y-3" data-testid="inline-insights-result">
          <div className="rounded-2xl bg-white border border-paper-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="label-overline">Reasoning</div>
              <span className={`ml-auto text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                insights.confidence === "high" ? "bg-sage-100 text-sage-800" :
                insights.confidence === "medium" ? "bg-amber-100 text-amber-800" :
                "bg-rose-100 text-rose-800"
              }`}>{insights.confidence}</span>
            </div>
            <p className="text-sm text-ink-900 leading-relaxed" data-testid="inline-insights-reasoning">
              {insights.reasoning}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <InlineSection title="Compensation patterns" items={insights.compensation_patterns} tone="amber" testid="inline-insights-compensation" />
            <InlineSection title="Related symptoms" items={insights.related_symptoms} tone="rose" testid="inline-insights-symptoms" />
            <InlineSection title="Evaluate next" items={insights.evaluate_next} tone="sage" testid="inline-insights-evaluate" />
            <InlineSection title="Treatment considerations" items={insights.treatment_considerations} tone="ink" testid="inline-insights-treatment" />
          </div>

          {insights.cautions?.length > 0 && (
            <InlineSection title="Cautions" items={insights.cautions} tone="rose" testid="inline-insights-cautions" caution />
          )}
        </div>
      )}
    </div>
  );
}

function labelOf(mid) {
  const m = MUSCLE_BY_ID[mid] || MUSCLE_BY_ID[canonicalMuscleId(mid)];
  return m?.label || mid;
}

function InlineSection({ title, items, tone, testid, caution }) {
  if (!items || items.length === 0) return null;
  const toneClass = {
    sage:  "border-sage-200 bg-sage-50/50",
    amber: "border-amber-200 bg-amber-50/60",
    rose:  "border-rose-200 bg-rose-50/60",
    ink:   "border-paper-200 bg-white",
  }[tone] || "border-paper-200 bg-white";
  return (
    <div className={`rounded-2xl border p-3.5 ${toneClass}`} data-testid={testid}>
      <div className="label-overline mb-1.5 flex items-center gap-1.5">
        {caution && <AlertTriangle className="w-3 h-3 text-rose-700" />}
        {title}
      </div>
      <ul className="space-y-1 list-disc list-inside text-xs text-ink-900 leading-relaxed">
        {items.slice(0, 6).map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}
